import { Record, RecordOf } from 'immutable';
import { get } from 'lodash';

import { StandardAction } from '../types';
import AuthAction from './auth.actions';
import { AuthState, IAuthPayload } from './types';

const initData = {
  initURL: '',
  accessToken: '',
  error: '',
  isSigningIn: false,
  isSigningUp: false,
  isSigningOut: false,
  isGettingToken: false,
  isResettingPassword: false,
  expires: 0,
};

const initialState = Record(initData)(initData);

export default class AuthReducer {
  static getReducer(state: RecordOf<AuthState> = initialState, action: StandardAction): RecordOf<AuthState> {
    switch (action.type) {
      // Sign Up
      case AuthAction.TYPES.SIGNUP.START:
      case AuthAction.TYPES.SIGNUP.SUCCESS:
      case AuthAction.TYPES.SIGNUP.FAILURE:
        return AuthReducer.handleSignUp(state, action);
      // Sign In
      case AuthAction.TYPES.SIGNIN.START:
      case AuthAction.TYPES.SIGNIN.SUCCESS:
      case AuthAction.TYPES.SIGNIN.FAILURE:
        return AuthReducer.handleSignIn(state, action);
      // Sign Out
      case AuthAction.TYPES.SIGNOUT.START:
      case AuthAction.TYPES.SIGNOUT.SUCCESS:
      case AuthAction.TYPES.SIGNOUT.FAILURE:
        return AuthReducer.handleSignOut(state, action);
      // Reset Password
      case AuthAction.TYPES.RESET_PASSWORD.START:
      case AuthAction.TYPES.RESET_PASSWORD.SUCCESS:
      case AuthAction.TYPES.RESET_PASSWORD.FAILURE:
        return AuthReducer.handleResetPassword(state, action);

      case AuthAction.TYPES.VERIFY_TOKEN.START:
      case AuthAction.TYPES.VERIFY_TOKEN.SUCCESS:
      case AuthAction.TYPES.VERIFY_TOKEN.FAILURE:
        return AuthReducer.handleVerifyToken(state, action);
      // Sync
      case AuthAction.TYPES.INIT_URL: {
        return { ...state, initURL: action.payload.initURL };
      }

      case AuthAction.TYPES.GET_NEW_ACCESS_TOKEN.START:
      case AuthAction.TYPES.GET_NEW_ACCESS_TOKEN.END:
        return AuthReducer.handleGetNewAccessToken(state, action);

      default:
        return state;
    }
  }

  static handleSignUp = (state: RecordOf<AuthState>, action: StandardAction<{ user: string }>): RecordOf<AuthState> => {
    switch (action.type) {
      case AuthAction.TYPES.SIGNUP.START:
        return {
          ...state,
          isSigningUp: true,
          error: '',
        };

      case AuthAction.TYPES.SIGNUP.SUCCESS:
        return {
          ...state,
          isSigningUp: false,
          accessToken: get(action, 'payload.user', ''),
        };

      case AuthAction.TYPES.SIGNUP.FAILURE:
        return {
          ...state,
          isSigningUp: false,
          error: action.error,
        };

      default:
        return state;
    }
  };

  static handleSignIn = (state: RecordOf<AuthState>, action: StandardAction<IAuthPayload>): RecordOf<AuthState> => {
    switch (action.type) {
      case AuthAction.TYPES.SIGNIN.START:
        return {
          ...state,
          isSigningIn: true,
          error: '',
        };

      case AuthAction.TYPES.SIGNIN.SUCCESS:
        return {
          ...state,
          isSigningIn: false,
          accessToken: action.payload?.accessToken,
          expires: action.payload?.expires,
        };

      case AuthAction.TYPES.SIGNIN.FAILURE:
        return {
          ...state,
          isSigningIn: false,
          error: action.error,
        };

      default:
        return state;
    }
  };

  static handleSignOut = (state: RecordOf<AuthState>, action: StandardAction<undefined>): RecordOf<AuthState> => {
    switch (action.type) {
      case AuthAction.TYPES.SIGNOUT.START:
        return {
          ...state,
          isSigningOut: true,
          error: '',
        };
      case AuthAction.TYPES.SIGNOUT.SUCCESS:
        return {
          ...state,
          isSigningOut: false,
          accessToken: '',
          expires: 0,
        };
      case AuthAction.TYPES.SIGNOUT.FAILURE:
        return {
          ...state,
          isSigningOut: false,
          error: action.error,
        };

      default:
        return state;
    }
  };

  static handleResetPassword = (state: RecordOf<AuthState>, action: StandardAction<undefined>): RecordOf<AuthState> => {
    switch (action.type) {
      case AuthAction.TYPES.RESET_PASSWORD.START:
        return state.set('isResettingPassword', true).set('error', '');

      case AuthAction.TYPES.RESET_PASSWORD.SUCCESS:
        return state.set('isResettingPassword', false);

      case AuthAction.TYPES.RESET_PASSWORD.FAILURE:
        return state.set('isResettingPassword', false);

      default:
        return state;
    }
  };

  static handleVerifyToken = (state: RecordOf<AuthState>, action: StandardAction): RecordOf<AuthState> => {
    switch (action.type) {
      case AuthAction.TYPES.VERIFY_TOKEN.START:
        return { ...state, error: '' };

      case AuthAction.TYPES.VERIFY_TOKEN.SUCCESS:
        return { ...state, error: '' };

      case AuthAction.TYPES.VERIFY_TOKEN.FAILURE:
        return { ...state, error: action.error };

      default:
        return state;
    }
  };

  static handleGetNewAccessToken = (state: RecordOf<AuthState>, action: StandardAction): RecordOf<AuthState> => {
    switch (action.type) {
      case AuthAction.TYPES.GET_NEW_ACCESS_TOKEN.START:
        return { ...state, isGettingToken: true };

      case AuthAction.TYPES.GET_NEW_ACCESS_TOKEN.END:
        return { ...state, isGettingToken: false };

      default:
        return state;
    }
  };
}
